import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Plus, Clock, CheckCircle, 
  AlertCircle, Loader2, ChevronRight, X, User,
  Headphones, FileText, CreditCard, Settings, HelpCircle,
  Paperclip, Download, ImageIcon
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API, getAuthHeaders } from '../utils/api';

const CATEGORIES = [
  { value: 'billing', label: 'Billing & Payments', icon: CreditCard, color: 'text-emerald-400' },
  { value: 'technical', label: 'Technical Issue', icon: Settings, color: 'text-blue-400' },
  { value: 'feature', label: 'Feature Request', icon: FileText, color: 'text-purple-400' },
  { value: 'other', label: 'Other', icon: HelpCircle, color: 'text-slate-400' }
];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  closed: { label: 'Closed', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-500/20 text-slate-400' },
  medium: { label: 'Medium', color: 'bg-amber-500/20 text-amber-400' },
  high: { label: 'High', color: 'bg-red-500/20 text-red-400' }
};

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function AttachmentView({ attachment }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = attachment.content_type?.startsWith('image/');

  useEffect(() => {
    let url;
    if (isImage) {
      axios.get(`${API}/files/${attachment.file_id}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        responseType: 'blob'
      }).then((res) => {
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [attachment.file_id, isImage]);

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/files/${attachment.file_id}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  if (isImage) {
    return blobUrl ? (
      <img
        src={blobUrl}
        alt={attachment.filename}
        onClick={handleDownload}
        className="mt-2 rounded-lg max-h-48 max-w-full cursor-pointer border border-zinc-700 hover:opacity-90 transition-opacity"
        data-testid="attachment-image"
      />
    ) : (
      <div className="mt-2 h-24 w-40 rounded-lg bg-zinc-800 animate-pulse" />
    );
  }

  return (
    <button
      onClick={handleDownload}
      className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800/70 border border-zinc-700 hover:bg-zinc-700/70 transition-colors text-xs text-slate-300"
      data-testid="attachment-file"
    >
      <FileText className="w-3.5 h-3.5 text-cyan-400" />
      <span className="truncate max-w-[180px]">{attachment.filename}</span>
      <Download className="w-3.5 h-3.5 text-slate-400" />
    </button>
  );
}

export default function Support() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'technical',
    priority: 'medium',
    message: ''
  });
  const [creatingTicket, setCreatingTicket] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, { 
        withCredentials: true, 
        headers: getAuthHeaders() 
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, { 
        withCredentials: true, 
        headers: getAuthHeaders() 
      });
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreatingTicket(true);
    try {
      const response = await axios.post(`${API}/tickets`, newTicket, { 
        withCredentials: true, 
        headers: getAuthHeaders() 
      });
      toast.success('Support ticket created successfully');
      setNewTicketOpen(false);
      setNewTicket({ subject: '', category: 'technical', priority: 'medium', message: '' });
      fetchTickets();
      fetchTicketDetails(response.data.ticket_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('File type not allowed. Use JPG, PNG, WEBP, GIF or PDF');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    setAttachFile(file);
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !attachFile) || !selectedTicket) return;

    setSendingMessage(true);
    try {
      if (attachFile) {
        const formData = new FormData();
        formData.append('file', attachFile);
        formData.append('message', newMessage);
        await axios.post(
          `${API}/tickets/${selectedTicket.ticket_id}/attachments`,
          formData,
          { withCredentials: true, headers: getAuthHeaders() }
        );
      } else {
        await axios.post(
          `${API}/tickets/${selectedTicket.ticket_id}/message`,
          { message: newMessage },
          { withCredentials: true, headers: getAuthHeaders() }
        );
      }
      setNewMessage('');
      setAttachFile(null);
      fetchTicketDetails(selectedTicket.ticket_id);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;

    try {
      await axios.post(
        `${API}/tickets/${selectedTicket.ticket_id}/close`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      toast.success('Ticket closed');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">Loading support tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Support Center</h1>
          <p className="text-slate-400">Get help from our support team</p>
        </div>
        <Button 
          onClick={() => setNewTicketOpen(true)}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="bg-[#0D131C] border-[#1E293B] p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
              Your Tickets
              {tickets.filter(t => t.status !== 'closed').length > 0 && (
                <Badge className="bg-cyan-500/20 text-cyan-400 border-0 ml-auto">
                  {tickets.filter(t => t.status !== 'closed').length} Active
                </Badge>
              )}
            </h3>

            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <Headphones className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No support tickets yet</p>
                <p className="text-slate-500 text-xs mt-1">Create one to get help</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => fetchTicketDetails(ticket.ticket_id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all border ${
                      selectedTicket?.ticket_id === ticket.ticket_id
                        ? 'bg-cyan-500/10 border-cyan-500/30'
                        : 'bg-zinc-900/50 border-transparent hover:bg-zinc-800/50 hover:border-zinc-700'
                    }`}
                    data-testid={`ticket-item-${ticket.ticket_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ticket.subject}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {ticket.ticket_id} • {formatTime(ticket.created_at)}
                        </p>
                      </div>
                      <Badge className={`text-[10px] ${STATUS_CONFIG[ticket.status]?.color}`}>
                        {STATUS_CONFIG[ticket.status]?.label}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Ticket Details / Chat */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card className="bg-[#0D131C] border-[#1E293B] flex flex-col h-[700px]">
              {/* Ticket Header */}
              <div className="p-4 border-b border-[#1E293B]">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedTicket.subject}</h3>
                    <div className="flex items-center gap-3 mt-2 text-sm text-slate-400">
                      <span>{selectedTicket.ticket_id}</span>
                      <span>•</span>
                      <Badge className={`text-xs ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                        {STATUS_CONFIG[selectedTicket.status]?.label}
                      </Badge>
                      <Badge className={`text-xs ${PRIORITY_CONFIG[selectedTicket.priority]?.color}`}>
                        {PRIORITY_CONFIG[selectedTicket.priority]?.label} Priority
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedTicket.status !== 'closed' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCloseTicket}
                        className="border-slate-700 text-slate-400 hover:bg-slate-800"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Resolved
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedTicket(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${
                      msg.sender === 'user' 
                        ? 'bg-cyan-600/20 border-cyan-500/30' 
                        : 'bg-emerald-600/20 border-emerald-500/30'
                    } border rounded-lg p-3`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${
                          msg.sender === 'user' ? 'text-cyan-400' : 'text-emerald-400'
                        }`}>
                          {msg.sender === 'user' ? 'You' : 'TradeForge Support'}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachment && <AttachmentView attachment={msg.attachment} />}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-4 border-t border-[#1E293B]">
                  {attachFile && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-zinc-800/70 border border-zinc-700 w-fit" data-testid="attachment-preview-chip">
                      {attachFile.type.startsWith('image/') ? (
                        <ImageIcon className="w-4 h-4 text-cyan-400" />
                      ) : (
                        <FileText className="w-4 h-4 text-cyan-400" />
                      )}
                      <span className="text-xs text-slate-300 truncate max-w-[200px]">{attachFile.name}</span>
                      <span className="text-[10px] text-slate-500">{(attachFile.size / 1024 / 1024).toFixed(1)}MB</span>
                      <button onClick={() => setAttachFile(null)} className="text-slate-400 hover:text-white" data-testid="remove-attachment-btn">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                      className="hidden"
                      data-testid="attachment-file-input"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-zinc-700 text-slate-400 hover:bg-zinc-800 shrink-0"
                      data-testid="attach-file-btn"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="bg-zinc-900 border-zinc-700 focus:border-cyan-500"
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      data-testid="ticket-message-input"
                    />
                    <Button 
                      onClick={handleSendMessage}
                      disabled={sendingMessage || (!newMessage.trim() && !attachFile)}
                      className="bg-cyan-600 hover:bg-cyan-700"
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card className="bg-[#0D131C] border-[#1E293B] p-8 flex flex-col items-center justify-center h-[700px]">
              <Headphones className="w-16 h-16 text-slate-600 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Welcome to Support</h3>
              <p className="text-slate-400 text-center mb-6 max-w-md">
                Select an existing ticket from the list or create a new one to get help from our team.
              </p>
              <Button 
                onClick={() => setNewTicketOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Ticket
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* New Ticket Dialog */}
      <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
        <DialogContent className="bg-[#0D131C] border-[#1E293B] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Create Support Ticket</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Subject *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Brief description of your issue"
                className="mt-1 bg-zinc-900 border-zinc-700"
                data-testid="ticket-subject-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select 
                  value={newTicket.category} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <cat.icon className={`w-4 h-4 ${cat.color}`} />
                          {cat.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Priority</Label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Message *</Label>
              <Textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Describe your issue in detail..."
                className="mt-1 bg-zinc-900 border-zinc-700 min-h-[120px]"
                data-testid="ticket-message-textarea"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setNewTicketOpen(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTicket}
                disabled={creatingTicket}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="submit-ticket-btn"
              >
                {creatingTicket ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
